/** @typedef {import('../index.js').BaseDocData} BaseDocData */
import { Timestamp } from 'firebase/firestore';

import { normalizeDate } from '../../utils/index.js';

/**
 * @typedef {Object} UserSchema
 * @property {string | null} email - Email of user.
 * @property {string | null} domain - Domain of user.
 * @property {string | null} organization - Organization the user belongs to.
 * @property {string | null} stripeCustomerId - Stripe customer ID.
 * @property {string | null} qboCustomerId - QuickBooks customer ID.
 * @property {Timestamp | Date | string | number | null} createdAt - Date the user was created. Legacy data may be a string; new data is a Firestore Timestamp.
 * @property {Timestamp} updatedAt - Date the user was last updated as a Firestore Timestamp: {@link https://firebase.google.com/docs/reference/node/firebase.firestore.Timestamp}
 *
 * @typedef {BaseDocData & UserSchema} UserDocData
*/

/**
 * Class representing a user.
 * @class
 */
export default class User {
  /** @type {UserDocData['validation']} */ validation = 'passed';
  /** Firestore document ID @type {UserDocData['id']} */ id;
  /** @type {UserDocData['email']} */ email;
  /** @type {UserDocData['domain']} */ domain;
  /** Optional organization field @type {UserDocData['organization']} */ organization;
  /** Optional Stripe customer ID @type {UserDocData['stripeCustomerId']} */ stripeCustomerId;
  /** Optional QuickBooks Online @type {UserDocData['qboCustomerId']} */ qboCustomerId;
  /** @type {UserDocData['createdAt']} */ createdAt;

  /**
   * Create a user.
   * @param {UserDocData} param0 - An {@link UserDocData} object.
   */
  constructor({
    id, email, domain, createdAt, organization, stripeCustomerId, qboCustomerId,
  }) {
    this.id = id || null;
    this.email = email || null;
    this.domain = domain || (email ? email.split('@')[1] : null);
    this.organization = organization || null;
    this.stripeCustomerId = stripeCustomerId || null;
    this.qboCustomerId = qboCustomerId || null;
    this.createdAt = normalizeDate(createdAt);
  }

  /**
   * Converts the {@link User} instance to a Firestore data object.
   * @returns {Omit<UserDocData, 'id'>} {@link UserDocData} (without `id`).
   */
  toFirestore({ serverTimestamp }) {
    const user = {
      email: this.email,
      domain: this.email ? this.email.split('@')[1] : null,
      createdAt: this.createdAt || serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    // Only include optional fields if they are set to avoid overwriting existing data with null
    if (this.organization) user.organization = this.organization;
    if (this.stripeCustomerId) user.stripeCustomerId = this.stripeCustomerId;
    if (this.qboCustomerId) user.qboCustomerId = this.qboCustomerId;
    return user;
  }

  /**
   * Creates a User instance from Firestore data.
   * @param {any} docData - Raw Firestore document.
   * @returns A {@link User} instance.
   */
  static fromFirestore(docData) {
    return new User(docData);
  }
}
